import { c as _c } from "react/compiler-runtime";
import type { UrlObject } from 'url';
import React from 'react';
import LinkPrimitive from 'next/link';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
const linkVariants = cva('focus-visible:underline focus-visible:shadow-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accessibility-ring', {
  variants: {
    variant: {
      default: 'active:underline disabled:text-primary-button-disabled',
      inline: 'rounded-sm underline hover:text-primary-button-hover hover:no-underline active:text-primary-button-pressed active:no-underline',
      secondary: 'support-styles-cta-tomato gap-2 rounded-sm bg-transparent p-1 text-secondary underline hover:text-secondary-button-hover hover:no-underline active:text-secondary-button-pressed active:no-underline',
      standalone: 'body-md-bold gap-2 rounded-sm bg-transparent p-1 text-primary-button-hover underline hover:text-primary hover:no-underline focus-visible:underline active:text-primary-button-hover active:no-underline',
      accordion: 'body-md-bold gap-2 whitespace-nowrap rounded-sm bg-transparent p-1 text-primary no-underline hover:text-primary-button-hover hover:underline focus-visible:underline active:text-primary-button-pressed active:underline ',
      breadcrumb: 'body-md-bold gap-2 rounded-sm bg-transparent p-1 text-primary underline hover:text-primary-button-hover hover:no-underline active:text-primary-button-pressed active:no-underline',
      'subcategory-badge': 'rounded-lg'
    },
    size: {
      sm: 'gap-1.5',
      md: 'gap-2',
      lg: 'gap-3'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'md'
  }
});
const iconSizeVariants = cva('', {
  variants: {
    size: {
      sm: 'size-3',
      md: 'size-3.5',
      lg: 'size-4'
    }
  },
  defaultVariants: {
    size: 'md'
  }
});
type LinkBehavior = 'auto' | 'external' | 'internal';
const isExternalUrl = (href: string | UrlObject, base?: string): boolean => {
  if (!base) return false;
  try {
    const hrefString = typeof href === 'string' ? href : href.href ?? '';
    const fullUrl = new URL(hrefString, base);
    return !fullUrl.href.startsWith(base);
  } catch {
    return false;
  }
};
interface LinkProps extends React.ComponentPropsWithoutRef<typeof LinkPrimitive>, VariantProps<typeof linkVariants> {
  iconSize?: VariantProps<typeof iconSizeVariants>['size'];
  linkBehavior?: LinkBehavior;
  showExternalIcon?: boolean;
  openInNewTab?: boolean;
  disabled?: boolean;
}
const Link = React.forwardRef<React.ElementRef<typeof LinkPrimitive>, LinkProps>((t0, ref) => {
  const $ = _c(34);
  let children;
  let className;
  let disabled;
  let href;
  let iconSize;
  let openInNewTab;
  let props;
  let showExternalIcon;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      href,
      iconSize,
      children,
      variant,
      linkBehavior: t1,
      showExternalIcon,
      openInNewTab,
      disabled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = disabled;
    $[4] = href;
    $[5] = iconSize;
    $[6] = openInNewTab;
    $[7] = props;
    $[8] = showExternalIcon;
    $[9] = t1;
    $[10] = variant;
  } else {
    children = $[1];
    className = $[2];
    disabled = $[3];
    href = $[4];
    iconSize = $[5];
    openInNewTab = $[6];
    props = $[7];
    showExternalIcon = $[8];
    t1 = $[9];
    variant = $[10];
  }
  const linkBehavior = t1 === undefined ? "auto" : t1;
  if (!href) {
    return null;
  }
  let t2;
  if ($[11] !== href || $[12] !== linkBehavior) {
    t2 = linkBehavior === "external" ? true : linkBehavior === "internal" ? false : isExternalUrl(href, process.env.NEXT_PUBLIC_BASE_URL);
    $[11] = href;
    $[12] = linkBehavior;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const detectedExternal = t2;
  const showIcon = showExternalIcon ?? detectedExternal;
  const newTab = openInNewTab ?? detectedExternal;
  let t3;
  if ($[14] !== className || $[15] !== disabled || $[16] !== showIcon || $[17] !== variant) {
    t3 = cn(linkVariants({
      variant
    }), {
      "inline-flex items-center gap-2": showIcon && variant !== "inline",
      "pointer-events-none text-primary-button-disabled underline": disabled
    }, className);
    $[14] = className;
    $[15] = disabled;
    $[16] = showIcon;
    $[17] = variant;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== newTab) {
    t4 = newTab && {
      target: "_blank",
      rel: "noopener noreferrer"
    };
    $[19] = newTab;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== iconSize || $[22] !== showIcon || $[23] !== variant) {
    t5 = showIcon && (variant === "inline" ? <span className="inline-flex items-center gap-1 whitespace-nowrap">{"\xA0"}<ExternalLink className={cn("inline align-text-bottom", iconSizeVariants({
        size: iconSize
      }))} aria-label="(Opens in a new tab)" /></span> : <ExternalLink className={cn("shrink-0", iconSizeVariants({
      size: iconSize
    }))} aria-label="(Opens in a new tab)" />);
    $[21] = iconSize;
    $[22] = showIcon;
    $[23] = variant;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== children || $[26] !== disabled || $[27] !== href || $[28] !== props || $[29] !== ref || $[30] !== t3 || $[31] !== t4 || $[32] !== t5) {
    t6 = <LinkPrimitive className={t3} aria-disabled={disabled} href={href} ref={ref} {...t4} {...props}>{children}{t5}</LinkPrimitive>;
    $[25] = children;
    $[26] = disabled;
    $[27] = href;
    $[28] = props;
    $[29] = ref;
    $[30] = t3;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  return t6;
});
Link.displayName = 'Link';
export { iconSizeVariants, Link, linkVariants, type LinkBehavior, type LinkProps };